import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Lightbulb } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import ComparisonChart from '../components/ComparisonChart';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { getFeatureDetail } from '../data/featureDetails';
import { FEATURE_SLUGS, FEATURE_BASE, SITE_URL } from '../data/site';
import imgLighterColour from '../assets/lighter-colour-concrete.jpg';
import imgPoreSize from '../assets/feat-pore-size.jpg';
import imgDurability from '../assets/feat-durability.jpg';
import imgAdhesion from '../assets/feat-adhesion-strength.jpg';
import imgWaterRetention from '../assets/feat-water-retention.jpg';
import imgAsr from '../assets/feat-asr-reduction.jpg';
import imgSulphate from '../assets/feat-sulphate-resistance.jpg';
import imgEfflorescence from '../assets/feat-efflorescence.jpg';
import imgChloride from '../assets/feat-chloride-migration.jpg';
import imgFreezeThaw from '../assets/feat-freeze-thaw.jpg';
import imgAcid from '../assets/feat-acid-resistance.jpg';
import imgCo2 from '../assets/feat-co2-reduction.jpg';

// Per-feature illustrative images with bilingual captions.
const FEATURE_MEDIA = {
  'pore-size': { src: imgPoreSize, caption: { de: 'Dichtes, wasserabweisendes Betongef\u00fcge mit stark reduzierter Kapillarporosit\u00e4t.', en: 'Dense, water-repellent concrete matrix with strongly reduced capillary porosity.' } },
  durability: { src: imgDurability, caption: { de: 'Dauerhafte Betoninfrastruktur mit langer rechnerischer Nutzungsdauer.', en: 'Durable concrete infrastructure with a long calculated service life.' } },
  'adhesion-strength': { src: imgAdhesion, caption: { de: 'Sichere Verbundhaftung d\u00fcnnschichtiger Kleber und M\u00f6rtel am Untergrund.', en: 'Reliable bond of thin-bed adhesives and mortars to the substrate.' } },
  'water-retention': { src: imgWaterRetention, caption: { de: 'Gleichm\u00e4\u00dfiger M\u00f6rtelauftrag auf saugendem Untergrund dank hoher Wasserr\u00fcckhaltung.', en: 'Even mortar application on absorbent substrates thanks to high water retention.' } },
  'asr-reduction': { src: imgAsr, caption: { de: 'Massige Wasserbauwerke: Alkalibindung senkt das Risiko der Alkali-Kiesel-Reaktion.', en: 'Mass hydraulic structures: alkali binding lowers the risk of the alkali\u2013silica reaction.' } },
  'sulphate-resistance': { src: imgSulphate, caption: { de: 'Fundamente in sulfathaltigem Boden mit erh\u00f6hter Widerstandsf\u00e4higkeit.', en: 'Foundations in sulphate-bearing soil with increased resistance.' } },
  efflorescence: { src: imgEfflorescence, caption: { de: 'Farbige Betonoberfl\u00e4chen bleiben frei von wei\u00dfen Kalkausbl\u00fchungen.', en: 'Coloured concrete surfaces stay free of white lime efflorescence.' } },
  'chloride-migration': { src: imgChloride, caption: { de: 'Meeres- und K\u00fcstenbauwerke: reduzierter Chlorideintrag sch\u00fctzt die Bewehrung.', en: 'Marine and coastal structures: reduced chloride ingress protects the reinforcement.' } },
  'lighter-colour': { src: imgLighterColour },
  'freeze-thaw': { src: imgFreezeThaw, caption: { de: 'Dichte Oberfl\u00e4chen widerstehen Frost-Tau-Wechseln und Taumitteln.', en: 'Dense surfaces resist freeze\u2013thaw cycles and de-icing agents.' } },
  'acid-resistance': { src: imgAcid, caption: { de: 'Beton in Abwasser- und Industrieanlagen mit erh\u00f6hter S\u00e4urebest\u00e4ndigkeit.', en: 'Concrete in wastewater and industrial facilities with increased acid resistance.' } },
  'co2-reduction': { src: imgCo2, caption: { de: 'Ressourcenschonende Betonfertigung mit reduziertem CO\u2082-Fu\u00dfabdruck.', en: 'Resource-efficient concrete production with a reduced CO\u2082 footprint.' } },
};

const FeatureDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const detail = getFeatureDetail(slug);
  const idx = FEATURE_SLUGS.indexOf(slug);

  if (!detail || idx === -1) return <Navigate to={FEATURE_BASE} replace />;

  const list = t('features.list');
  const feature = list[idx] || { title: slug, desc: '' };
  const d = detail[lang];
  const chart = detail.chart;
  const media = FEATURE_MEDIA[slug];
  const featureImg = media?.src;
  const imgCaption = detail.image?.caption?.[lang] || media?.caption?.[lang];

  const prevSlug = idx > 0 ? FEATURE_SLUGS[idx - 1] : null;
  const nextSlug = idx < FEATURE_SLUGS.length - 1 ? FEATURE_SLUGS[idx + 1] : null;
  const prevTitle = prevSlug ? list[idx - 1]?.title : null;
  const nextTitle = nextSlug ? list[idx + 1]?.title : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${feature.title} – metakaolin`,
    description: feature.desc,
    about: 'metakaolin',
    inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}${FEATURE_BASE}/${slug}`,
    author: { '@type': 'Organization', name: 'Newchem GmbH' },
    publisher: { '@type': 'Organization', name: 'Newchem GmbH', url: SITE_URL },
  };

  const barLabel = (b) =>
    b.highlight
      ? lang === 'de' ? 'Mit Metakaolin' : 'With metakaolin'
      : lang === 'de' ? 'Referenz (OPC)' : 'Reference (OPC)';

  return (
    <div>
      <SEO
        title={`${feature.title} – ${lang === 'de' ? 'Eigenschaft von Metakaolin' : 'metakaolin feature'}`}
        description={feature.desc}
        path={`${FEATURE_BASE}/${slug}`}
        lang={lang}
        keywords={SEO_KEYWORDS[lang]}
        jsonLd={jsonLd}
      />
      <PageHeader label={t('features.label')} title={feature.title} lead={feature.desc} imageKey="concreteArch2" />

      <section className="bg-white py-14 lg:py-20">
        <div className="container-wide">
          <Link to={FEATURE_BASE} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-blue-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('features.label')}
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* Text */}
            <div>
              <div className="space-y-4 text-[15px] text-slate-600 leading-relaxed">
                {d.intro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            {/* Chart */}
            <Reveal>
              <ComparisonChart
                caption={chart.unit[lang]}
                bars={chart.bars.map((b) => ({ label: barLabel(b), value: b.value, display: b.display, highlight: b.highlight }))}
              />
            </Reveal>
          </div>

          {/* Feature illustration */}
          {featureImg && (
            <Reveal>
              <figure className="mt-12 max-w-5xl">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                  <img src={featureImg} alt={imgCaption || feature.title} className="w-full h-64 sm:h-80 lg:h-[420px] object-cover" data-testid="feature-image" />
                </div>
                {imgCaption && <figcaption className="mt-3 text-[12px] text-slate-500 italic">{imgCaption}</figcaption>}
              </figure>
            </Reveal>
          )}

          {/* Practical example */}
          <div className="mt-12 max-w-4xl rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:p-8">
            <div className="flex items-center gap-3">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-700/5 border border-blue-700/15 text-blue-700">
                <Lightbulb className="w-5 h-5" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">{d.example.title}</h2>
            </div>
            <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{d.example.text}</p>
          </div>

          {/* Prev / Next */}
          <div className="mt-12 flex items-stretch justify-between gap-4">
            {prevSlug ? (
              <Link to={`${FEATURE_BASE}/${prevSlug}`} className="group flex-1 max-w-[46%] flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-blue-300 transition">
                <ArrowLeft className="w-4 h-4 text-blue-700 shrink-0" />
                <span className="text-left">
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{lang === 'de' ? 'Zurück' : 'Previous'}</span>
                  <span className="block text-[14px] font-semibold text-slate-800 group-hover:text-blue-700">{prevTitle}</span>
                </span>
              </Link>
            ) : <span className="flex-1 max-w-[46%]" />}
            {nextSlug ? (
              <Link to={`${FEATURE_BASE}/${nextSlug}`} className="group flex-1 max-w-[46%] flex items-center justify-end gap-3 p-4 rounded-xl border border-slate-200 hover:border-blue-300 transition text-right">
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{lang === 'de' ? 'Weiter' : 'Next'}</span>
                  <span className="block text-[14px] font-semibold text-slate-800 group-hover:text-blue-700">{nextTitle}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-blue-700 shrink-0" />
              </Link>
            ) : <span className="flex-1 max-w-[46%]" />}
          </div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default FeatureDetail;
